import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import Feather from '@react-native-vector-icons/feather/static';
import { GTFonts, useTheme } from '../../theme';
import { borderWidth, fs, iconSize, s } from '../../utils/libs';
import {
  LINKED_BADGE_COLORS,
  PERSON_AVATAR_INITIALS_COLOR,
  getPersonAvatarColor,
  getPersonInitials,
} from '../../utils/personAvatar';

interface PersonAvatarProps {
  name: string;
  /** Diameter before scaling; 40 for list rows and staff chips. */
  size?: number;
  /** Customer exists in the back office: the small cloud badge. */
  linked?: boolean;
  /** Blue ring and tick, for an assigned staff member. */
  selected?: boolean;
  /** Grey circle with a person outline, for the walk in row. */
  anonymous?: boolean;
  /** Background override; otherwise derived from the name. */
  tint?: string;
}

/** Initials in a tinted circle: customers, contacts and staff look alike. */
const PersonAvatar: React.FC<PersonAvatarProps> = ({
  name,
  size = 40,
  linked = false,
  selected = false,
  anonymous = false,
  tint,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme, size), [theme, size]);
  const background = anonymous
    ? theme.colors.surfaceSecondary
    : tint || getPersonAvatarColor(name);

  return (
    <View
      style={[
        styles.circle,
        { backgroundColor: background },
        selected && styles.circleSelected,
      ]}>
      {anonymous ? (
        <Feather
          name="user"
          size={iconSize(Math.round(size * 0.5))}
          color={theme.colors.textGray600}
        />
      ) : (
        <Text style={styles.initials}>{getPersonInitials(name)}</Text>
      )}
      {linked ? (
        <View style={styles.cloudBadge}>
          <FontAwesome6
            name="cloud"
            iconStyle="solid"
            size={fs(7)}
            color={LINKED_BADGE_COLORS.icon}
          />
        </View>
      ) : null}
      {selected ? (
        <View style={styles.tickBadge}>
          <Feather
            name="check"
            size={iconSize(10)}
            color={theme.colors.textInverse}
          />
        </View>
      ) : null}
    </View>
  );
};

const createStyles = (theme: any, size: number) => {
  const diameter = s(size);
  return StyleSheet.create({
    circle: {
      width: diameter,
      height: diameter,
      borderRadius: diameter / 2,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray200,
    },
    circleSelected: {
      borderWidth: 2.5,
      borderColor: theme.colors.primaryDark,
    },
    initials: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(Math.round(size * 0.35)),
      lineHeight: fs(Math.round(size * 0.45)),
      color: PERSON_AVATAR_INITIALS_COLOR,
    },
    cloudBadge: {
      position: 'absolute',
      right: -s(3),
      bottom: -s(3),
      width: s(17),
      height: s(17),
      borderRadius: s(9),
      borderWidth: borderWidth(2),
      borderColor: theme.colors.surface,
      backgroundColor: LINKED_BADGE_COLORS.background,
      alignItems: 'center',
      justifyContent: 'center',
    },
    tickBadge: {
      position: 'absolute',
      right: -s(4),
      bottom: -s(4),
      width: s(19),
      height: s(19),
      borderRadius: s(10),
      borderWidth: borderWidth(2),
      borderColor: theme.colors.surface,
      backgroundColor: theme.colors.primaryDark,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
};

export default PersonAvatar;
